import { useCallback, useMemo } from 'react'
import api, { PROJECT_UUID_HEADER } from '../../lib/commons/api'
import { TicketRepository } from '../../applications/ports/ticketRepository'
import {
  CreateTicketRequest,
  ticketService,
  UpdateTicketDeltaRequest,
} from '../../domain/entity/ticket'
import {
  REFERENCED_ENTITY_SEARCH_LIMIT,
  ReferencedEntityWithIcon,
} from '../../domain/value-object/ReferencedEntity'

export const useTicketRepository = (): TicketRepository => {
  const fetchByWbsItemUuid = useCallback(async (wbsItemUuid: string) => {
    const response = await api.functional.request(
      'GET',
      '/api/v1/tickets/detail/wbs-item-uuid',
      { wbsItemUuid },
      true
    )
    return ticketService.fromApiResponseToEntity(response.json)
  }, [])
  const searchAsReferencedEntity = useCallback(
    async (
      projectUuid: string,
      ticketTypes: string[] | undefined,
      searchText: string
    ): Promise<ReferencedEntityWithIcon[]> => {
      const response = await api.functional.request(
        'GET',
        '/api/v1/tickets/referenced_values/find',
        {
          projectUuid,
          ticketTypes,
          all: searchText,
          limit: REFERENCED_ENTITY_SEARCH_LIMIT,
        },
        true
      )
      return response.json.data as ReferencedEntityWithIcon[]
    },
    []
  )
  const fetchAsReferencedEntity = useCallback(
    async (uuid: string): Promise<ReferencedEntityWithIcon | undefined> => {
      const response = await api.functional.request(
        'GET',
        '/api/v1/tickets/referenced_values',
        { uuid },
        true
      )
      return response.json
    },
    []
  )
  const create = useCallback(
    async (projectUuid: string, request: CreateTicketRequest) => {
      const response = await api.functional.request(
        'POST',
        '/api/v1/tickets',
        request,
        true,
        null,
        { [PROJECT_UUID_HEADER]: projectUuid }
      )
      return { response }
    },
    []
  )
  const updateDelta = useCallback(
    async (projectUuid: string, request: UpdateTicketDeltaRequest) => {
      const response = await api.functional.request(
        'PUT',
        '/api/v1/tickets/delta',
        request,
        true,
        null,
        { [PROJECT_UUID_HEADER]: projectUuid }
      )
      return { response }
    },
    []
  )

  const repository = useMemo(
    () => ({
      fetchByWbsItemUuid,
      searchAsReferencedEntity,
      fetchAsReferencedEntity,
      create,
      updateDelta,
    }),
    [
      fetchByWbsItemUuid,
      searchAsReferencedEntity,
      fetchAsReferencedEntity,
      create,
      updateDelta,
    ]
  )
  return repository
}
